/**
 * The screen a signed-in person sees when they have no dashboard access.
 *
 * WHAT THIS REPLACED
 * ------------------
 * A red panel headed "CANNOT REACH THE DASHBOARD API", carrying the sentence
 * "This account has not been invited to a VoiceProp dashboard", above a "Try
 * again" button that could never succeed — the request was correctly refused,
 * not failing. So the screen was wrong three times over: it blamed the API for
 * a permission decision, it did not say which email address was being refused,
 * and its only offered action was to repeat the same request forever.
 *
 * On 31 Aug 2026 Shaun Mellor hit this for the second time. He has two Clerk
 * identities and was signing in with the office inbox; his own grant was
 * healthy throughout. Nothing on screen named the address he was signed in as,
 * so he could not see it and neither could anyone reading his email.
 *
 * THE RULE
 * --------
 * Always show the address they are signed in as. Always give an action that
 * can actually change the outcome — switch account, or a support email that
 * arrives already carrying the diagnosis. Never offer a retry for a decision.
 */
import { useClerk, useUser } from '@clerk/clerk-react';
import { c, border, radius, mono } from '../theme/tokens';
import type { AccessDenial } from '../api/types';
import { Lockup } from './Lockup';

/**
 * The screen itself takes plain data and callbacks, with the Clerk session
 * supplied by the connected wrapper at the bottom of this file.
 *
 * That split is not ceremony. Clerk's hooks assert they are inside a live
 * ClerkProvider, so a component that calls them directly cannot be rendered
 * anywhere except a real signed-in browser — which means the copy on the most
 * user-hostile screen in the product could not be reviewed without recreating
 * a locked-out account. The wording is the entire point of this screen, so it
 * has to be reviewable offline.
 */
export function NoAccessView({
  denial,
  signedInAs,
  fallbackMessage,
  onSwitchAccount,
  supportHref,
}: {
  denial: AccessDenial | null;
  signedInAs: string | null;
  fallbackMessage?: string;
  onSwitchAccount: () => void;
  supportHref: string;
}) {
  const title = denial?.title ?? 'This account does not have access to a VoiceProp dashboard';
  const detail =
    denial?.detail
    ?? fallbackMessage
    ?? 'VoiceProp is invite-only. If you were invited on a different email address, sign out and sign in with that '
      + 'one. Otherwise contact ModelProp support and quote this address and your company name.';

  // Default both to true: when the server could not diagnose, offering the two
  // actions that can actually resolve it is strictly better than offering none.
  const showSwitch = denial?.suggestSwitchAccount ?? true;
  const showSupport = denial?.suggestSupport ?? true;

  return (
    <main
      style={{
        minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 28, background: c.surface,
      }}
    >
      <section
        style={{
          width: 'min(560px, 100%)', background: c.surface2, border: border.default,
          borderRadius: radius.card, padding: '34px 34px 30px',
          display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 16px 48px rgba(9,64,116,0.10)',
        }}
      >
        <Lockup />

        <div>
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', color: c.ink3 }}>
            DASHBOARD ACCESS
          </div>
          <h1 style={{ margin: '10px 0 0', fontSize: 25, lineHeight: 1.3, color: c.ink, textWrap: 'pretty' }}>
            {title}
          </h1>
        </div>

        {/*
          The single most important element on this screen. Shaun's two support
          tickets both existed because this line was missing: he was signing in
          on one address and telling us about another, and nobody could see it.
        */}
        {signedInAs ? (
          <div
            style={{
              display: 'flex', flexDirection: 'column', gap: 4,
              background: c.lineSoft, borderRadius: radius.button, padding: '13px 16px',
            }}
          >
            <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: '0.14em', color: c.ink3 }}>
              YOU ARE SIGNED IN AS
            </span>
            <strong style={{ fontSize: 16, color: c.ink, wordBreak: 'break-all' }}>{signedInAs}</strong>
          </div>
        ) : null}

        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: c.ink2, maxWidth: '52ch', textWrap: 'pretty' }}>
          {detail}
        </p>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
          {showSwitch ? (
            <button
              className="vp-reset"
              onClick={onSwitchAccount}
              style={{
                padding: '11px 18px', borderRadius: radius.button, border: 'none',
                background: c.indigo, color: '#fff', fontSize: 14.5, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Sign in with a different email
            </button>
          ) : null}

          {showSupport ? (
            <a
              href={supportHref}
              className="vp-outline"
              style={{
                padding: '11px 18px', borderRadius: radius.button, border: border.default,
                fontSize: 14.5, color: c.ink2, textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
              }}
            >
              Email ModelProp support
            </a>
          ) : null}
        </div>

        <p style={{ margin: '4px 0 0', fontSize: 12.5, lineHeight: 1.6, color: c.ink3 }}>
          Support replies during UK office hours. The email button fills in your address and the reason
          automatically, so we can fix it without a back-and-forth.
        </p>
      </section>
    </main>
  );
}

/** Mirrors supportMailto() on the server, for the client-only fallback path. */
function mailto(denial: AccessDenial | null, email: string | null): string {
  const body = [
    'Hello ModelProp support,',
    '',
    'I cannot access the VoiceProp dashboard.',
    '',
    `Signed in as: ${denial?.signedInAs ?? email ?? 'unknown'}`,
    `Reason given: ${denial?.code ?? 'unknown'}`,
    '',
    'My company is: ',
  ].join('\n');
  return `mailto:support@modelprop.ai?subject=${encodeURIComponent('VoiceProp dashboard access')}&body=${encodeURIComponent(body)}`;
}

/** Connected screen: supplies the Clerk session to the view above. */
export function NoAccess({ denial, fallbackMessage }: { denial: AccessDenial | null; fallbackMessage?: string }) {
  const clerk = useClerk();
  const { user } = useUser();

  // Clerk knows the signed-in address even if the API call failed before the
  // server could tell us. Never render this screen without naming the account —
  // that omission is the whole reason it was useless.
  const clerkEmail = user?.primaryEmailAddress?.emailAddress ?? null;

  return (
    <NoAccessView
      denial={denial}
      fallbackMessage={fallbackMessage}
      signedInAs={denial?.signedInAs ?? clerkEmail}
      supportHref={mailto(denial, clerkEmail)}
      // Sign out and return to the sign-in card, so "use your other address" is
      // one click rather than an instruction to follow.
      onSwitchAccount={() => void clerk.signOut({ redirectUrl: '/dashboard/' })}
    />
  );
}
